<template>
  <DashboardCard title="Billing Frequency Analysis">
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Frequency Distribution -->
      <div class="lg:col-span-2">
        <h4 class="text-sm font-medium text-gray-700 mb-4">
          Frequency Breakdown
        </h4>
        <div class="space-y-3">
          <div
            v-for="frequency in frequencyBreakdown"
            :key="frequency.type"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <div class="flex items-center">
              <div
                class="w-3 h-3 rounded-full mr-3"
                :style="{ backgroundColor: frequency.color }"
              ></div>
              <div>
                <p class="font-medium text-gray-900">{{ frequency.label }}</p>
                <p class="text-sm text-gray-600">{{ frequency.description }}</p>
              </div>
            </div>
            <div class="text-right">
              <div class="text-lg font-semibold text-gray-900">
                {{ frequency.count }}
              </div>
              <div class="text-xs text-gray-500">
                {{ frequency.percentage }}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Key Statistics -->
      <div>
        <h4 class="text-sm font-medium text-gray-700 mb-4">Statistics</h4>
        <div class="space-y-4">
          <div class="text-center p-4 bg-blue-50 rounded-lg">
            <div class="text-2xl font-bold text-blue-600">
              {{ totalTenants }}
            </div>
            <div class="text-xs text-blue-600">Total Tenants</div>
          </div>

          <div class="text-center p-4 bg-green-50 rounded-lg">
            <div class="text-2xl font-bold text-green-600">
              {{ mostCommonFrequency }}
            </div>
            <div class="text-xs text-green-600">Most Popular</div>
          </div>

          <div class="text-center p-4 bg-yellow-50 rounded-lg">
            <div class="text-2xl font-bold text-yellow-600">
              {{ frequencyDiversity }}%
            </div>
            <div class="text-xs text-yellow-600">Diversity Index</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Billing Pattern Analysis -->
    <div class="mt-6 pt-6 border-t border-gray-200">
      <h4 class="text-sm font-medium text-gray-700 mb-4">
        Billing Pattern Insights
      </h4>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="p-4 border border-gray-200 rounded-lg">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700"
              >Monthly Billing</span
            >
            <span class="text-sm text-blue-600">{{ monthlyPercentage }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="bg-blue-600 h-2 rounded-full"
              :style="{ width: monthlyPercentage + '%' }"
            ></div>
          </div>
          <p class="text-xs text-gray-500 mt-1">{{ monthlyInsight }}</p>
        </div>

        <div class="p-4 border border-gray-200 rounded-lg">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700"
              >Long-term Billing</span
            >
            <span class="text-sm text-green-600"
              >{{ longTermPercentage }}%</span
            >
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="bg-green-600 h-2 rounded-full"
              :style="{ width: longTermPercentage + '%' }"
            ></div>
          </div>
          <p class="text-xs text-gray-500 mt-1">{{ longTermInsight }}</p>
        </div>
      </div>
    </div>

    <!-- Plan Usage Summary -->
    <div class="mt-6 pt-6 border-t border-gray-200">
      <h4 class="text-sm font-medium text-gray-700 mb-4">
        Tenant Behavior Summary
      </h4>
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div class="text-lg font-semibold text-gray-900">
              {{ activeFrequencyTypes }}
            </div>
            <div class="text-xs text-gray-500">Active Frequency Types</div>
          </div>
          <div>
            <div class="text-lg font-semibold text-gray-900">
              {{ commitmentLevel }}
            </div>
            <div class="text-xs text-gray-500">Commitment Level</div>
          </div>
          <div>
            <div class="text-lg font-semibold text-gray-900">
              {{ flexibilityScore }}%
            </div>
            <div class="text-xs text-gray-500">Flexibility Score</div>
          </div>
          <div>
            <div class="text-lg font-semibold text-gray-900">
              {{ stabilityScore }}%
            </div>
            <div class="text-xs text-gray-500">Stability Score</div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between items-center text-sm text-gray-600">
        <span>Total Active Tenants: {{ totalTenants }}</span>
        <span>Most Common: {{ mostCommonFrequency }}</span>
        <button
          @click="viewDetails"
          class="text-blue-600 hover:text-blue-800 font-medium"
        >
          View Detailed Breakdown →
        </button>
      </div>
    </template>
  </DashboardCard>
</template>

<script>
import DashboardCard from "./DashboardCard.vue";

export default {
  name: "FrequencyInsightsWidget",
  components: {
    DashboardCard
  },
  props: {
    frequencyData: {
      type: Object,
      required: true,
      default: () => ({
        monthly_active_tenants: 0,
        yearly_active_tenants: 0,
        triennial_active_tenants: 0,
        other_frequency_tenants: 0,
        total_active_tenants: 0
      })
    }
  },
  computed: {
    totalTenants() {
      return this.frequencyData?.total_active_tenants || 0;
    },

    frequencyBreakdown() {
      const data = [
        {
          type: "monthly",
          label: "Monthly Billing",
          description: "Billed every month",
          count: this.frequencyData?.monthly_active_tenants || 0,
          color: "#3B82F6"
        },
        {
          type: "yearly",
          label: "Annual Billing",
          description: "Billed once per year",
          count: this.frequencyData?.yearly_active_tenants || 0,
          color: "#10B981"
        },
        {
          type: "triennial",
          label: "Triennial Billing",
          description: "Billed every 3 years",
          count: this.frequencyData?.triennial_active_tenants || 0,
          color: "#F59E0B"
        },
        {
          type: "other",
          label: "Other Frequencies",
          description: "Custom billing cycles",
          count: this.frequencyData?.other_frequency_tenants || 0,
          color: "#EF4444"
        }
      ];

      return data.map(item => ({
        ...item,
        percentage:
          this.totalTenants > 0
            ? ((item.count / this.totalTenants) * 100).toFixed(1)
            : 0
      }));
    },

    mostCommonFrequency() {
      const frequencies = this.frequencyBreakdown;
      if (frequencies.length === 0) return "None";

      const max = frequencies.reduce((prev, current) =>
        current.count > prev.count ? current : prev
      );

      return max.label;
    },

    monthlyPercentage() {
      return this.totalTenants > 0
        ? (
            ((this.frequencyData?.monthly_active_tenants || 0) /
              this.totalTenants) *
            100
          ).toFixed(1)
        : 0;
    },

    longTermPercentage() {
      const longTerm =
        (this.frequencyData?.yearly_active_tenants || 0) +
        (this.frequencyData?.triennial_active_tenants || 0);
      return this.totalTenants > 0
        ? ((longTerm / this.totalTenants) * 100).toFixed(1)
        : 0;
    },

    activeFrequencyTypes() {
      const activeTypes = this.frequencyBreakdown.filter(f => f.count > 0)
        .length;
      return activeTypes;
    },

    commitmentLevel() {
      const longTermTenants =
        (this.frequencyData?.yearly_active_tenants || 0) +
        (this.frequencyData?.triennial_active_tenants || 0);
      const percentage =
        this.totalTenants > 0 ? (longTermTenants / this.totalTenants) * 100 : 0;

      if (percentage >= 75) return "High";
      if (percentage >= 50) return "Medium";
      if (percentage >= 25) return "Low";
      return "Very Low";
    },

    flexibilityScore() {
      // Higher monthly percentage = higher flexibility
      return this.monthlyPercentage;
    },

    stabilityScore() {
      // Higher long-term percentage = higher stability
      return this.longTermPercentage;
    },

    frequencyDiversity() {
      // Calculate how diverse the billing frequencies are
      const activeTypes = this.frequencyBreakdown.filter(f => f.count > 0)
        .length;
      const maxTypes = this.frequencyBreakdown.length;
      return ((activeTypes / maxTypes) * 100).toFixed(0);
    },

    monthlyInsight() {
      const pct = parseFloat(this.monthlyPercentage);
      if (pct >= 75) return "High flexibility preference";
      if (pct >= 50) return "Moderate flexibility";
      if (pct >= 25) return "Some monthly subscribers";
      return "Low monthly adoption";
    },

    longTermInsight() {
      const pct = parseFloat(this.longTermPercentage);
      if (pct >= 75) return "Strong commitment tendency";
      if (pct >= 50) return "Good long-term adoption";
      if (pct >= 25) return "Some committed users";
      return "Limited long-term commitment";
    }
  },
  methods: {
    viewDetails() {
      // Prepare detailed frequency analysis data
      const detailsData = {
        frequencyDetails: this.frequencyBreakdown.map(freq => ({
          ...freq,
          cycle: this.getBillingCycleDescription(freq.type)
        })),
        flexibilityAnalysis: this.generateFlexibilityAnalysis(),
        commitmentAnalysis: this.generateCommitmentAnalysis(),
        businessImpact: this.generateBusinessImpact(),
        recommendations: this.generateRecommendations(),
        totalTenants: this.totalTenants,
        activeFrequencyTypes: this.frequencyBreakdown.filter(f => f.count > 0)
          .length,
        dominantFrequency: this.mostCommonFrequency,
        diversityScore: this.frequencyDiversity
      };

      this.$emit("view-details", detailsData);
    },

    getBillingCycleDescription(type) {
      const cycles = {
        monthly: "Every month",
        yearly: "Once per year",
        triennial: "Every 3 years",
        other: "Custom cycle"
      };
      return cycles[type] || "Unknown";
    },

    generateFlexibilityAnalysis() {
      const monthlyPct = parseFloat(this.monthlyPercentage);
      if (monthlyPct >= 75) {
        return `${monthlyPct}% of tenants prefer monthly billing, indicating strong preference for flexibility and ability to adjust plans frequently.`;
      } else if (monthlyPct >= 50) {
        return `${monthlyPct}% use monthly billing, showing moderate preference for flexibility with some commitment to longer terms.`;
      } else if (monthlyPct >= 25) {
        return `${monthlyPct}% choose monthly billing, indicating limited preference for short-term flexibility.`;
      } else {
        return `Only ${monthlyPct}% use monthly billing, showing very low preference for flexibility.`;
      }
    },

    generateCommitmentAnalysis() {
      const longTermPct = parseFloat(this.longTermPercentage);
      const triennialCount = this.frequencyData?.triennial_active_tenants || 0;
      const yearlyCount = this.frequencyData?.yearly_active_tenants || 0;

      if (longTermPct >= 50) {
        return `${longTermPct}% choose long-term billing (${yearlyCount} yearly, ${triennialCount} triennial), demonstrating strong commitment and platform trust.`;
      } else if (longTermPct >= 25) {
        return `${longTermPct}% prefer long-term billing, showing moderate commitment levels from your most dedicated users.`;
      } else if (longTermPct > 0) {
        return `${longTermPct}% choose long-term billing, indicating limited but valuable commitment from ${triennialCount +
          yearlyCount} tenants.`;
      } else {
        return `No tenants currently use long-term billing, suggesting preference for maintaining flexibility or lack of long-term incentives.`;
      }
    },

    generateBusinessImpact() {
      const monthlyPct = parseFloat(this.monthlyPercentage);
      const activeTypes = this.frequencyBreakdown.filter(f => f.count > 0)
        .length;

      if (monthlyPct >= 75) {
        return `High monthly adoption provides consistent revenue flow but may increase churn risk. Consider introducing annual incentives.`;
      } else if (activeTypes >= 3) {
        return `Diverse billing preferences indicate mature customer base with varied needs, requiring more complex billing management.`;
      } else if (activeTypes === 2) {
        return `Two billing frequencies provide good balance between operational simplicity and customer choice.`;
      } else {
        return `Single billing frequency simplifies operations but may limit customer acquisition from different segments.`;
      }
    },

    generateRecommendations() {
      const monthlyPct = parseFloat(this.monthlyPercentage);
      const longTermPct = parseFloat(this.longTermPercentage);

      if (monthlyPct >= 75) {
        return `Consider introducing annual discounts (10-20% savings) to encourage longer commitments and improve revenue predictability.`;
      } else if (longTermPct >= 50) {
        return `Leverage high commitment levels by introducing multi-year discounts or exclusive features for long-term subscribers.`;
      } else {
        return `Monitor billing pattern trends and consider A/B testing different discount structures to optimize revenue per customer.`;
      }
    }
  }
};
</script>
